import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CircularProgress, Grid, Typography, makeStyles, createStyles, Theme as AugmentedTheme } from '@material-ui/core';
import { calculateField, CustomInputMappingField, JsonDataType, CustomInputMapping, generateTitle } from '@tuqqi/common';
import Form from './components/Form';
import Congrats from './components/Congrats';
import Banner from './components/Banner';
import Error from './components/Error';
import { FroalaDescription } from './components/FroalaDescription';
import { ILocation } from './components/CustomInput/Inputypes/Location/locationHelpers';
interface IProps {
    uid: string
}
interface IData {
    uid: string,
    title: string,
    description: string,
    inactiveMessage: string,
    inactiveTitle: string,
    finishMessage: string,
    state: number
    fieldsToShow: string | null
    type: object,
}

interface ISendData {
    title?: string,
    externalInputUid: string,
    locationAddress?: ILocation['locationAddress'],
    geoLocation?: ILocation['geoLocation'],
    captcha: string,
    jsonData: string,
    businessId?: string,
    fileAttachments?: any[],
    relatedBusinessIds? : {
        id: string,
        datatype: string,
    }[],
}

const getDefaultDisabledMessage = (formTitle: string) => {
    if(!formTitle) return `The form no longer accepts responses. If you think this is an error, try contacting support.`
    return `The "${formTitle}" form no longer accepts responses. If you think this is an error, try contacting support.`
}

const getDefaultDisabledTitle = (formTitle: string) => {
    if(!formTitle) return `The form is no longer active`
    return `The form "${formTitle}" is no longer active`
}

const useStyles = makeStyles((theme: AugmentedTheme) => createStyles({
    wrapper: {
        padding: '20px 15px 50px',
        fontFamily: 'Google Sans, Roboto, Helvetica, Arial, sans-serif',
        minHeight: '100vh'
    },
    blockHolder: {
        maxWidth: '640px',
        width: '100%',
        margin: '0 auto',
    },
    captchaNotification: {
        fontSize: 22,
        fontWeight: 600,
        color: 'red',
        marginTop: 20,
    },
    formTitle: {
        fontSize: 32,
        fontWeight: 400,
        color: '#202124',
        lineHeight: '135%',
    },
    formDescription: {
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '.2px',
        lineHeight: '20px',
        color: '#202124',
        marginTop: '12px',
        // direction: 'rtl',
        textAlign: 'right'
    },
    formHyperLink: {
        color:'rgb(166, 84, 198)',
        cursor: 'pointer'
    },
    formControl: {
        margin: '12px 0',
        backgroundColor: '#fff',
        border: '1px solid #dadce0',
        borderRadius: 8,
        padding: '22px 24px 24px',
        width: '100%',
        boxSizing: 'border-box' as 'border-box',
        overflow: 'hidden',
    },
    formControlBorder: {
        borderTop: '8px solid #8645f9',
    },
}))

declare global {
    interface Window {
        grecaptcha: any;
    }
}

function Main({ uid }: IProps) {
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isErrorUidRequest, setIsErrorUidRequest] = useState<boolean>(false);
    const [inputValues, setInputValues] = useState<{}>({});
    const [data, setData] = useState<IData>({ type: {}, uid: '', title: '', description: '', fieldsToShow: '',finishMessage: '', inactiveTitle: '', inactiveMessage: '', state: 0});
    const [titleValue, setTitleValue] = useState<string>('');
    const [allowUploadFiles, setAllowUploadFiles] = useState<boolean>(false);
    const [withLocation, setWithLocation] = useState<boolean>(false);
    const [signature, setSignature] = useState<string>('');
    const [policy, setPolicy] = useState<string>('');
    const [requestServerUrl, setRequestServerUrl] = useState<string>('');
    const [inputFields, setIputFields] = useState<CustomInputMappingField[]>([]);
    const [captchaNotification, setCaptchaNotification] = useState<boolean>(false);
    const [businessIdValue, setBusinessIdValue] = useState<string>('');
    const [captchaResponseFormServer, setCaptchaResponseFormServer] = useState<boolean>(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
    const [attachments, setAttachments] = useState<[]>([]);
    const [bannerLink, setBannerLink] = useState<string | null>(null);
    const [isAutomaticField, setIsAutomaticField] = useState<boolean>(false);
    const [captchaValue, setCaptchaValue] = useState<string | null>('');
    const [automaticOrderFields, setAutomaticOrderFields] = useState<string[]>(['']);
    const [dataTypeFields, setDataTypeFields] = useState<CustomInputMappingField[] | []>([]);
    const [dataTypes, setDataTypes] = useState<{}>({});
    const [isRtl, setIsRtl] = useState<boolean>(false)
    const [location, setLocation] = useState<ILocation | null>(null)

    const isDisabledForm = useMemo(() => data.state !== 0, [data.state])

    const formLogicCondition = !isFormSubmitted || !captchaResponseFormServer || captchaNotification;
    const captchaIsWrongCondition = isFormSubmitted && !captchaResponseFormServer;

    const getCalculatedFields = useCallback((data: JsonDataType) => {
        if(!inputFields?.length) return {}
        const fields = inputFields.filter(x => x.type === 'calculation')
        const calculated: {[key in string]: string} = {}
        
        fields.forEach(f => {
            const [value] = calculateField(f, data)
            calculated[f.sourceField] = value
        })

        return calculated
    }, [inputFields])
    
    const fieldChanged = (value: string | string[], sourceField: string, isBusinessId: boolean, fieldType?: string) => {
        setInputValues(currentValues => {
            if (!isBusinessId) {
                //@ts-ignore
                currentValues[sourceField] = value;
            } else {
                setBusinessIdValue(value.toString())
            }

            const calculatedData = getCalculatedFields(currentValues)

            return {...currentValues, ...calculatedData};
        });

        if(fieldType && fieldType === 'datatype') {
            setDataTypes(
                currentValues => {
                    //@ts-ignore
                    currentValues[sourceField] = value
                    return currentValues;
                }
            )
        }
    }

    const getUploadFiles = (fileData: []) => {
        setAttachments(prev => [...prev, ...fileData]);
    }

    const removeUploadFile = (fileDataKey: string) => {
        //@ts-ignore
        setAttachments(prev => prev.filter(x => x.key !== fileDataKey));
    }

    const addSignature = (fileData: any) => {
        //@ts-ignore
        setAttachments(prev => [...prev, fileData]);
    }

    const titleChanged = (value: string) => {
        setTitleValue(value)
    }

    const getProcessedDescription = (description: string) => {

        if(!description){
            return <Typography className={classes.formDescription}>External input description</Typography>
        }
        
        return <FroalaDescription mailHtmlContent={description} />
    }

    const handleLoaded = () => {
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute("6LdZrBwcAAAAAOxKnSN_re2ZO6tkIJ-uM0Tej5kF", { action: "homepage" })
                .then((token: string) => {
                    setCaptchaValue(token);
                })
        })
    }

    const handleSubmit = () => {
        if (!captchaValue) {
            setCaptchaNotification(true);
            setIsFormSubmitted(false);
            return
        }

        if (captchaNotification) {
            setCaptchaNotification(false);
        }

        const formData: ISendData = {
            externalInputUid: data.uid,
            jsonData: JSON.stringify({ ...inputValues }),
            captcha: captchaValue,
        };

        if (attachments.length) {

            const fileAttachments = attachments.map((item: any) => {
                return {
                    fileUid: item.key,
                    fileName: item.filename,
                    fileType: item.key?.includes?.("signature-image-temp.png") ? 'hidden' : item.mimetype,
                    fileSize: item.size,
                    thumbnailLink: `https://drive-thirdparty.googleusercontent.com/64/type/${item.mimetype}`
                }
            })

            formData.fileAttachments = fileAttachments;
        }

        if(!!location && withLocation) {
            formData.locationAddress = location.locationAddress
            formData.geoLocation = location.geoLocation
        }

        if(automaticOrderFields && automaticOrderFields.length) {
            let values = [];

            for(let i = 0; i < automaticOrderFields.length; i++) {
                //@ts-ignore
                const value = inputValues[automaticOrderFields[i]];
                value.length && values.push(value)
            }
           
            if(isAutomaticField) {
                const mapping = {
                    customDataMapping: {
                        customFieldsMappings: inputFields,
                    },
                    isWithoutSeparateTitleByCommas: false
                } 
                formData.title = generateTitle(inputValues as JsonDataType, mapping as CustomInputMapping,[], formData.businessId)
            } 
        }

       if(!isAutomaticField) {
            formData.title = titleValue;
        }


        if (businessIdValue.length) {
            formData.businessId = businessIdValue;
            //@ts-ignore
            delete inputValues['Business Id'];
            formData.jsonData = JSON.stringify({ ...inputValues });
        }

        if(dataTypeFields.length) {
            let dataTypeConfig = [];
            
            for (let prop in dataTypes) {
                const data = {
                    datatype: '',
                    id: ''
                };
                //@ts-ignore
                data.id = dataTypes[prop];
 
                const field= dataTypeFields.find((item: CustomInputMappingField) => item.sourceField === prop);

                if(field?.dataType) {
                    data.datatype = field.dataType;
                }
                
                dataTypeConfig.push(data);
              }

              formData.relatedBusinessIds = dataTypeConfig;
        }

        window.grecaptcha
        .execute("6LdZrBwcAAAAAOxKnSN_re2ZO6tkIJ-uM0Tej5kF", { action: "homepage" })
        .then((token: string) => {
            // setCaptchaValue(token);            
            formData.captcha = token;
            axios({
                method: 'POST',
                url: `${requestServerUrl}externalinput`,
                data: formData
            }).then((response) => {
                if (response.status === 200) {
                    setCaptchaResponseFormServer(response.data.isCaptchaValid);
                    setIsFormSubmitted(true);
                }
            }).catch((e) => console.error(e));

        })

        
    }

    // Load data
    useEffect(() => {
        const fetchData = async () => {
            if (requestServerUrl && captchaValue && data && data.uid !== uid) {
                const result = await axios(
                    `${requestServerUrl}externalinput?externalInputUid=${uid}&recaptcha=${captchaValue}`,
                );

                setIsAutomaticField(result.data.isAutomaticTitle);

                setData(result.data);
                setIsRtl(result.data.isRTL)
                setAllowUploadFiles(!!result.data?.isEnableFiles)
                setWithLocation(!!result.data?.isEnableLocation)
                let fields: CustomInputMappingField[] = result.data.type.customFieldsMappings

                if(!!result.data.fieldsToShow) {
                    const allowedFields = result.data.fieldsToShow.split(";")
                    fields = fields.filter((f) => allowedFields.includes(f.sourceField))
                }
                
                setIputFields(fields);
                setSignature(result.data.signature);
                setPolicy(result.data.policy);
                result.data.type.customFieldsMappings && setIsLoading(false);
                setIsErrorUidRequest(false);
                handleLoaded();
            }
        };

        fetchData().catch(e => {
            console.log(e);
            setIsErrorUidRequest(true);
            }
        );
    }, [uid, requestServerUrl, captchaValue, data]);



    useEffect(() => {
        const fetchData = async () => {
            if (requestServerUrl && captchaValue && data && data.uid === uid && !bannerLink) {

                const resultImage = await axios(
                    `${requestServerUrl}externalinput/file?externalInputUid=${uid}&recaptcha=${captchaValue}`,
                );
                
                if(resultImage.config.url) {
                    setBannerLink(resultImage.config.url)
                }

                handleLoaded();
            }
        };

        fetchData().catch(e => console.log(e));
    }, [uid, requestServerUrl, captchaValue, bannerLink, data]);

    useEffect(() => {
        setInputValues(currentValues => {
            const newValues = inputFields.reduce((obj, field) => {
                // @ts-ignore
                obj[field.sourceField] = "";
                return obj;
            }, {});
            return Object.assign({}, newValues, currentValues);
        });

        const fieldsWithAutomaticTitle = inputFields.filter((item: CustomInputMappingField) => item.automaticTitleOrder !== null)
        .map((item:CustomInputMappingField) => item.sourceField);
        setAutomaticOrderFields(fieldsWithAutomaticTitle);

        const dataTypeFields = inputFields.filter((item: CustomInputMappingField) => item.type === 'datatype');
        dataTypeFields.length && setDataTypeFields(dataTypeFields);
    }, [inputFields,data])


    useEffect(() => {
        const script = document.createElement("script")
        script.src = "https://www.google.com/recaptcha/api.js?render=6LdZrBwcAAAAAOxKnSN_re2ZO6tkIJ-uM0Tej5kF"
        script.addEventListener("load", handleLoaded)
        document.body.appendChild(script)
    }, [])

    useEffect(() => {
        const URL: string | undefined = process.env.REACT_APP_STAGE === 'Dev' ? process.env.REACT_APP_DEV_API_URL : process.env.REACT_APP_PROD_API_URL;
        URL && setRequestServerUrl(URL);
        setIsLoading(true);
    }, [])

    useEffect(() => {
        document.title = data.title || 'External input title'
    }, [data.title])

    const CongratsMessage = <Congrats title={data.title || 'External input title'} finishMessage={data.finishMessage} />

    const LoadingInfo = <>{isErrorUidRequest ? <Error/> : <CircularProgress size={30} />}</>

    const FormInfoDisabled = <Grid container alignItems="center" direction="column" className={classes.blockHolder}>
        <Grid container direction="column" className={`${classes.formControl} ${classes.formControlBorder}`}>
        <Typography dir="auto" variant="h1" className={classes.formTitle}>{data.inactiveTitle || getDefaultDisabledTitle(data.title)}</Typography>
        <div style={{ 
            direction: isRtl ? 'rtl' : 'ltr',
            textAlign: isRtl ? 'right' : 'left' 
        }}>
            {getProcessedDescription(data.inactiveMessage || getDefaultDisabledMessage(data.title))}
        </div>
        </Grid>
    </Grid>
    
    const FormInfoActive = <>
    <Banner bannerLink={bannerLink} />
    <Grid container alignItems="center" direction="column" className={classes.blockHolder}>
        <Grid container direction="column" className={`${classes.formControl} ${classes.formControlBorder}`}>
            <Typography dir="auto"
                variant="h1" className={classes.formTitle}>{data.title || 'External input title'}</Typography>
        <div style={{ 
                direction: isRtl ? 'rtl' : 'ltr',
                textAlign: isRtl ? 'right' : 'left' 
            }}  >
            {getProcessedDescription(data.description)}
        </div>
        </Grid>
        <Form
            location={location}
            setLocation={setLocation}
            allowUploadFiles={allowUploadFiles}
            withLocation={withLocation}
            getCalculatedFields={getCalculatedFields}
            inputValues={inputValues}
            businessIdValue={businessIdValue}
            handleSubmit={handleSubmit}
            inputFields={inputFields}
            fieldChanged={fieldChanged}
            titleChanged={titleChanged}
            signature={signature}
            policy={policy}
            isRtl={isRtl}
            addSignature={addSignature}
            getUploadFiles={getUploadFiles}
            removeUploadFile={removeUploadFile}
            attachments={attachments}
            isAutomaticField={isAutomaticField}
        />
        {captchaIsWrongCondition && <Typography className={classes.captchaNotification}>Captcha is wrong! Please, try again.</Typography>}
    </Grid>
</>

    const FormInfo = <>{isDisabledForm ? FormInfoDisabled : FormInfoActive}</>

    const LoadedInfo = <>{formLogicCondition ? FormInfo : CongratsMessage}</>

    return (
        <Grid className={classes.wrapper}>
                <Grid container alignItems="center" direction="column" className={classes.blockHolder}>
                    {isLoading ? 
                        LoadingInfo : 
                        LoadedInfo
                    }
                </Grid>
        </Grid>
    );
}

export default Main;
